/*
 * Product configuration gallery — Swiper freeMode carousel + config CTA.
 *
 * CTA applies a curated full variant from custom.product_gallery_variants:
 *   - Same product → sync options + set variant on the purchase form
 *   - Sibling metal → click product-group pill, then apply options after
 *     product-form:swap-product
 * Falls back to navigating to data-variant-url when in-place apply fails.
 *
 * Slide widths come from CSS (`slidesPerView: 'auto'`) to match Figma
 * card sizes (365 desktop / 320 mobile).
 *
 * Swiper is the global UMD at assets/swiper-bundle.min.js.
 */

const Swiper = window.Swiper;
const SELECTOR = '[data-configuration-gallery]';
const INSTANCE_KEY = '_configGalleryInstance';
const PPF_KEY = '_productPurchaseFormInstance';
const SWAP_EVENT = 'product-form:swap-product';

/**
 * Buffers a variant selection made in the gallery when a CTA triggers a metal
 * switch: the click resolves before product-form:swap-product fires, so the
 * apply is deferred until the swap lands (see the SWAP_EVENT listener below).
 * @type {{ options: string[], variantId: string, url: string } | null}
 */
let pendingApply = null;

class ConfigurationGallery {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    this.swiper = null;
    /** @type {((event: Event) => void) | null} */
    this._onCtaClick = null;
    /** @type {((event: KeyboardEvent) => void) | null} */
    this._onCtaKeydown = null;
    this.init();
  }

  init() {
    this._initSwiper();
    this._bindCtas();
  }

  _initSwiper() {
    if (!Swiper) return;

    const swiperEl = this.root.querySelector('.swiper');
    if (!swiperEl) return;

    const ds = this.root.dataset;
    const gapD = parseFloat(ds.gapD) || 8;
    const gapT = parseFloat(ds.gapT) || 8;
    const gapM = parseFloat(ds.gapM) || 8;

    this.swiper = new Swiper(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: gapM,
      simulateTouch: true,
      freeMode: {
        enabled: true,
        sticky: false,
      },
      mousewheel: {
        forceToAxis: true,
      },
      // Trackpad / wheel scrolls the strip in freeMode without click-drag.
      // forceToAxis: only horizontal gestures move it (vertical scrolls the
      // page); releaseOnEdges: hand vertical page scroll back at the ends.
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true,
      },
      grabCursor: true,
      watchOverflow: true,
      watchSlidesProgress: true,
      breakpoints: {
        750: {
          slidesPerView: 'auto',
          spaceBetween: gapT,
          freeMode: {
            enabled: true,
            sticky: false,
          },
        },
        990: {
          slidesPerView: 'auto',
          spaceBetween: gapD,
          freeMode: {
            enabled: true,
            sticky: false,
          },
        },
      },
    });
  }

  _bindCtas() {
    this._onCtaClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const card = target.closest('[data-config-gallery-cta]');
      if (!(card instanceof HTMLElement) || !this.root.contains(card)) return;

      // Swiper flags a drag/swipe by setting allowClick = false; ignore that
      // synthetic click so a scroll gesture never fires the CTA (which would
      // scroll to top). A genuine tap leaves allowClick true.
      if (this.swiper && this.swiper.allowClick === false) return;

      event.preventDefault();
      this._shopConfiguration(card);
    };

    this._onCtaKeydown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.hasAttribute('data-config-gallery-cta')) return;
      if (!this.root.contains(target)) return;

      event.preventDefault();
      this._shopConfiguration(target);
    };

    this.root.addEventListener('click', this._onCtaClick);
    this.root.addEventListener('keydown', this._onCtaKeydown);
  }

  /**
   * @param {HTMLElement} card
   */
  _shopConfiguration(card) {
    const variantId = card.dataset.variantId || '';
    const handle = card.dataset.productHandle || card.dataset.siblingHandle || '';
    const fallbackUrl =
      card.dataset.variantUrl || card.dataset.siblingUrl || '';
    const options = this._parseOptions(card.dataset.options);

    this._scrollToAtf();

    const currentHandle = this._currentProductHandle();

    // Legacy image-block CTA: metal-only sibling, no variant id.
    if (!variantId && handle) {
      const anchor = this._siblingAnchor(handle);
      if (anchor) {
        anchor.click();
        return;
      }
      if (fallbackUrl) {
        window.location.href = fallbackUrl;
      }
      return;
    }

    if (!variantId && !options.length) {
      if (fallbackUrl) window.location.href = fallbackUrl;
      return;
    }

    // Same product (or no handle) — apply in place.
    if (!handle || !currentHandle || handle === currentHandle) {
      const applied = applyVariant(options, variantId);
      if (!applied && fallbackUrl) {
        window.location.href = fallbackUrl;
      }
      return;
    }

    // Different metal sibling — switch product, then apply on swap-product.
    const anchor = this._siblingAnchor(handle);
    if (!anchor) {
      if (fallbackUrl) window.location.href = fallbackUrl;
      return;
    }

    pendingApply = { options, variantId, url: fallbackUrl };
    anchor.click();

    // Safety: if swap never fires (navigation fallback / failed switch),
    // clear pending after a beat so a later metal click isn't poisoned.
    window.setTimeout(() => {
      if (pendingApply?.variantId === variantId) {
        pendingApply = null;
      }
    }, 8000);
  }

  /**
   * @param {string | undefined} raw
   * @returns {string[]}
   */
  _parseOptions(raw) {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch (_e) {
      return [];
    }
  }

  /**
   * @returns {string}
   */
  _currentProductHandle() {
    const jsonNode = document.querySelector('.product-purchase-form__product-json');
    if (jsonNode instanceof HTMLElement && jsonNode.dataset.productHandle) {
      return jsonNode.dataset.productHandle;
    }
    const path = window.location.pathname || '';
    const match = path.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  /**
   * @param {string} handle
   * @returns {HTMLAnchorElement | null}
   */
  _siblingAnchor(handle) {
    if (!handle) return null;
    const anchor = document.querySelector(
      `product-group-selector-component a[data-sibling-handle="${CSS.escape(handle)}"]`
    );
    return anchor instanceof HTMLAnchorElement ? anchor : null;
  }

  _scrollToAtf() {
    const main =
      document.querySelector('[id^="shopify-section-"][id$="__main"]') ||
      document.querySelector('product-group-selector-component') ||
      document.querySelector('h1');

    if (main instanceof HTMLElement) {
      main.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  destroy() {
    if (this._onCtaClick) {
      this.root.removeEventListener('click', this._onCtaClick);
      this._onCtaClick = null;
    }
    if (this._onCtaKeydown) {
      this.root.removeEventListener('keydown', this._onCtaKeydown);
      this._onCtaKeydown = null;
    }
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

/**
 * @param {string[]} options
 * @param {string} variantId
 * @returns {boolean}
 */
function applyVariant(options, variantId) {
  const ppfRoot = document.querySelector('.product-purchase-form');
  const ppf = ppfRoot && ppfRoot[PPF_KEY];
  if (!ppf) return false;

  if (options.length && typeof ppf.syncOptionSelection === 'function') {
    // Mirror metal-switch: non-compound first, then full selection so
    // compound accordion hidden selects land correctly.
    const optionNames = ppf.productJson?.options || [];
    if (optionNames.length) {
      const nonCompound = options.map((value, i) => {
        const name = optionNames[i] || '';
        return String(name).indexOf(':') !== -1 ? null : value;
      });
      ppf.syncOptionSelection(nonCompound);
    }
    ppf.syncOptionSelection(options);
  }

  if (typeof window.initCompoundAccordions === 'function') {
    window.initCompoundAccordions(document, ppf.variants || []);
  }

  let variant = null;
  if (variantId && Array.isArray(ppf.variants)) {
    variant = ppf.variants.find((v) => String(v.id) === String(variantId)) || null;
  }
  // Cross-metal: curated variant id belongs to the sibling product — match by
  // option values against the live (repointed) matrix instead.
  if (!variant && options.length && typeof ppf._findVariant === 'function') {
    variant = ppf._findVariant(options);
  }
  if (!variant) return false;

  if (typeof ppf._setCurrentVariant === 'function') {
    ppf._setCurrentVariant(variant);
    return true;
  }

  return false;
}

// Module-level: apply the buffered selection once the metal switch commits.
document.addEventListener(SWAP_EVENT, () => {
  const pending = pendingApply;
  if (!pending) return;
  pendingApply = null;
  const applied = applyVariant(pending.options, pending.variantId);
  if (!applied && pending.url) {
    window.location.href = pending.url;
  }
});

/**
 * @param {ParentNode} [root]
 */
function initAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el[INSTANCE_KEY]) return;
    el[INSTANCE_KEY] = new ConfigurationGallery(el);
  });
}

/**
 * @param {ParentNode} [root]
 */
function destroyAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const instance = el[INSTANCE_KEY];
    if (instance) {
      instance.destroy();
      el[INSTANCE_KEY] = null;
    }
  });
}

function boot() {
  if (!window.Swiper) {
    requestAnimationFrame(boot);
    return;
  }
  initAll();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// Theme editor re-renders sections via shopify:section:load/unload; re-init on load.
// Use Element (not ParentNode) — ParentNode is a mixin and not a browser global.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) initAll(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  if (event.target instanceof Element) destroyAll(event.target);
});
